import React from "react";

export interface QuizStats {
  totalQuestions: number;
  answeredQuestions: number;
  skippedQuestions: number;
  viewedQuestions: number;
  unvisitedQuestions: number;
  completionPercentage: number;
}

interface ConfirmationFormProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  stats?: QuizStats;
  timeRemaining?: string;
}

const ConfirmationForm: React.FC<ConfirmationFormProps> = ({
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Submit Quiz",
  cancelLabel = "Continue Quiz",
  stats,
  timeRemaining,
}) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: 600, width: '100%' }}>
      <div className="modal-content">
        <div className="modal-header bg-info text-white">
          <h5 className="modal-title fw-bold">
            <i className="fas fa-chart-bar me-2"></i>
            Quiz Submission Summary
          </h5>
        </div>
        <div className="modal-body">
          {stats && (
            <div>
              {/* Quiz Stats Overview */}
              <div className="text-center mb-4">
                <h6 className="fw-bold mb-3">Your Quiz Performance Summary</h6>
                <div className="row text-center">
                  <div className="col-3">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-3">
                        <div className="text-info fw-bold fs-4">{stats.answeredQuestions}</div>
                        <div className="small text-muted">Completed</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-3">
                        <div className="text-warning fw-bold fs-4">{stats.skippedQuestions}</div>
                        <div className="small text-muted">Skipped</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-3">
                        <div className="text-secondary fw-bold fs-4">{stats.viewedQuestions}</div>
                        <div className="small text-muted">Viewed</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="card border-0 bg-light">
                      <div className="card-body p-3">
                        <div className="text-muted fw-bold fs-4">{stats.unvisitedQuestions}</div>
                        <div className="small text-muted">Unvisited</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Completion Percentage */}
                <div className="mt-4">
                  <h5 className="fw-bold text-primary">
                    Quiz Completion: {stats.completionPercentage}%
                  </h5>
                  <div className="progress" style={{ height: '10px' }}>
                    <div 
                      className="progress-bar bg-info" 
                      style={{ width: `${stats.completionPercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Warnings for skipped/unvisited questions */}
              {(stats.skippedQuestions > 0 || stats.viewedQuestions > 0 || stats.unvisitedQuestions > 0) && (
                <div className="alert alert-warning border-0" style={{ borderRadius: '12px' }}>
                  <h6 className="fw-bold mb-2">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Important Notice
                  </h6>
                  <ul className="mb-2">
                    {stats.skippedQuestions > 0 && (
                      <li>
                        <strong>{stats.skippedQuestions} skipped question{stats.skippedQuestions > 1 ? 's' : ''}</strong>
                      </li>
                    )}
                    {stats.viewedQuestions > 0 && (
                      <li>
                        <strong>{stats.viewedQuestions} viewed but unanswered question{stats.viewedQuestions > 1 ? 's' : ''}</strong>
                      </li>
                    )}
                    {stats.unvisitedQuestions > 0 && (
                      <li>
                        <strong>{stats.unvisitedQuestions} unvisited question{stats.unvisitedQuestions > 1 ? 's' : ''}</strong>
                      </li>
                    )}
                  </ul>
                  <p className="mb-0 text-primary">
                    <i className="fas fa-info-circle me-1"></i>
                    <strong>Note:</strong> You will not receive any marks for these questions and it will impact your final performance percentage.
                  </p>
                </div>
              )}

              {/* Success message for complete quiz */}
              {stats.completionPercentage === 100 && (
                <div className="alert alert-success border-0" style={{ borderRadius: '12px' }}>
                  <h6 className="fw-bold mb-1">
                    <i className="fas fa-check-circle me-2"></i>
                    Excellent!
                  </h6>
                  <p className="mb-0">You have answered all questions. Your quiz is ready for submission.</p>
                </div>
              )}

              {/* Time remaining info */}
              {timeRemaining && (
                <div className="text-center mt-3 p-2 bg-light rounded">
                  <small className="text-muted">
                    <i className="fas fa-clock me-1"></i>
                    Time Remaining: <strong>{timeRemaining}</strong>
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer justify-content-between">
          <button 
            type="button" 
            className="btn btn-outline-secondary"
            onClick={onCancel}
          >
            <i className="fas fa-arrow-left me-1"></i>
            {cancelLabel}
          </button>
          <button 
            type="button" 
            className="btn btn-success fw-bold"
            onClick={onConfirm}
          >
            <i className="fas fa-check me-1"></i>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default ConfirmationForm;
